import { VehicleFeature } from '@/catalog/types/vehicle';
import { ICONS_MAP } from '@/common/utils/category-mappings';
import { cn } from '@/shadcn-ui/utils';
import Spacer from '@/common/components/spacer';
import { useMobileSpecifications } from '@/catalog/hooks/use-mobile-specifications';

interface SpecificationGroupProps {
  groupedSpecifications?: Record<string, VehicleFeature[]>;
  isFullSpecificationListView?: boolean;
}

export default function SpecificationGroup({
  groupedSpecifications,
  isFullSpecificationListView = false,
}: SpecificationGroupProps) {
  const mobileSpecs = useMobileSpecifications(groupedSpecifications);

  if (!groupedSpecifications || !Object.keys(groupedSpecifications).length) return null;

  return (
    <>
      {isFullSpecificationListView ? <Spacer variant="height8" /> : null}

      <h2 className="pdp-header text-[22px] leading-[32px] text-flexcar-neutral1 md:text-[32px] md:leading-normal">
        Specs
      </h2>

      {isFullSpecificationListView ? <Spacer variant="height24" /> : null}

      {/* Desktop View */}
      <div
        className={cn('w-full grid-cols-1 gap-x-[72px] gap-y-[48px] xl:w-[984px]', {
          'hidden md:grid md:grid-cols-2': !isFullSpecificationListView,
          'grid grid-cols-1': isFullSpecificationListView,
        })}
      >
        {Object.entries(groupedSpecifications).map(([category, specs]) => {
          const IconComponent = ICONS_MAP[category as keyof typeof ICONS_MAP];

          return (
            <div key={category}>
              <h4 className="pdp-header mb-6 flex flex-col items-start gap-4 text-[18px] leading-[32px] text-flexcar-neutral1 sm:text-[18px] md:text-[22px]">
                {IconComponent ? (
                  <IconComponent className="h-[24px] w-[24px] md:h-[32px] md:w-[32px]" />
                ) : null}
                {category}
              </h4>

              <div className="flex flex-col gap-[39px]">
                {specs.map((spec) => (
                  <div
                    key={spec.key}
                    className="flex justify-between border-b border-flexcar-neutral70 pb-[8px]"
                  >
                    <p className="body2-tight text-flexcar-neutral1">
                      {spec.display_name}
                    </p>
                    <p className="body2-tight font-semibold text-flexcar-neutral1">
                      {/* TODO: we still need to work on getting these values to be presentable either through a BE or FE change */}
                      {spec.display_value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile View */}
      {!isFullSpecificationListView ? (
        <div className="flex w-full flex-col gap-[12px] rounded-2xl bg-flexcar-neutral100 md:hidden">
          {mobileSpecs.map((spec, index) => (
            <div
              key={spec.key}
              className={cn('flex justify-between pb-[12px]', {
                'border-b border-flexcar-neutral70': index !== mobileSpecs.length - 1,
              })}
            >
              <span className="text-[16px] text-flexcar-neutral1">
                {spec.display_name}
              </span>
              <span className="text-[16px] font-semibold text-flexcar-neutral1">
                {spec.display_value}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}
